import React, { useEffect, useRef, useState } from 'react';
import { useFormik } from "formik";
import * as Yup from "yup";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import api from "../../api/index";
import { useTranslation } from 'react-i18next';
import useDisableBrowserBack from "../hooks/useDisableBrowserBack";
import useReadCookies from "../hooks/useReadCookies";

const NewGameSchema = Yup.object().shape({
  gameCode1: Yup.number()
    .min(0)
    .max(9)
    .required('Required'),
  gameCode2: Yup.number()
    .min(0)
    .max(9)
    .required('Required'),
  gameCode3: Yup.number()
    .min(0)
    .max(9)
    .required('Required'),
  gameCode4: Yup.number()
    .min(0)
    .max(9)
    .required('Required'),
});

function Onboard() {
  useDisableBrowserBack();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const refCode1 = useRef(null);
  const refCode2 = useRef(null);
  const refCode3 = useRef(null);
  const refCode4 = useRef(null);
  let messageRef = useRef(null);
  const mobileContainerRef = useRef(null);

  const [footerTopPosition, setFooterTopPosition] = useState(0);

  let { game_code } = useReadCookies();
  game_code = game_code || '';

  let gc1 = '', gc2 = '', gc3 = '', gc4 = '';
  if (game_code !== '') {
    gc1 = game_code[0];
    gc2 = game_code[1];
    gc3 = game_code[2];
    gc4 = game_code[3];
  }

  let initialValues = { gameCode1: gc1, gameCode2: gc2, gameCode3: gc3, gameCode4: gc4 };

  let handleSubmit = (values, { setSubmitting }) => {
    let gameCode = `${values.gameCode1}${values.gameCode2}${values.gameCode3}${values.gameCode4}`;
    api.getGameByCode(gameCode).then((response) => {
      if (response.status === 200) {
        const data = response.data;
        if (_.isEmpty(data)) {
          setSubmitting(false);
          setShowErrorPopup(true);
        } else {
          Cookies.set("game_id", data.secret, { expires: 1 / 24 });
          Cookies.set("game_code", data.game_code, { expires: 1 / 24 });
          setTimeout(() => {
            window.location.href = `/app/game/register/${data.secret}${window.location.search}`;
          }, 500);
        }
      }
    });
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: NewGameSchema,
    onSubmit: handleSubmit,
    validateOnMount: true,
  });

  const hideErrorPopup = () => {
    setShowErrorPopup(false);
    formik.setFieldValue('gameCode1', '');
    formik.setFieldValue('gameCode2', '');
    formik.setFieldValue('gameCode3', '');
    formik.setFieldValue('gameCode4', '');
  };

  const handleBackButton = (e) => {
    e.preventDefault();
    navigate('/app/game/intro/2' + window.location.search)
  }

  useEffect(() => {
    const updateResize = (mobileContainerRef) => {
      if (mobileContainerRef) {
        const containerBCR = mobileContainerRef.getBoundingClientRect();
        const appHeight = containerBCR.height;
        let updatedPosition;

        if (window.visualViewport) {
          const visualViewport = window.visualViewport;
          updatedPosition = visualViewport.height + window.scrollY;
          if (visualViewport.height > 1000 || visualViewport.width > 500) {
            updatedPosition = appHeight;
          }
        } else {
          updatedPosition = appHeight;
        }
        setFooterTopPosition(updatedPosition);
      }
    }
    const updateInterval = setInterval(updateResize, 2, mobileContainerRef.current);
    updateResize();
    return () => clearInterval(updateInterval);
  }, [setFooterTopPosition]);

  return (
    <div className="mobileContainer backgroundPaper onboard-screen" ref={mobileContainerRef}>

      <form className='game-form' onSubmit={formik.handleSubmit}>
        <div className='form-wrapper'>
          <div className='fx-ring-container'>
            <div className='fx-ring'></div>
            <div className='fx-ring-inner'></div>
            <div className='fx-ring-outer'></div>
          </div>
          <h3 className='form-title'>{t("student.game_code.title")}</h3>
          <div className='input-wrapper'>
            <div className='segmented-background'></div>
            <input
              className='segmented-input big-input'
              ref={refCode1}
              type="text"
              inputMode="numeric"
              autoComplete="false"
              maxLength="1"
              size="1"
              name="gameCode1"
              value={formik.values.gameCode1}
              onBlur={formik.handleBlur}
              onFocus={e => {
                formik.setFieldValue('gameCode1', '');
              }}
              onChange={e => {
                if (isNaN(e.target.value)) {
                  return;
                }
                formik.handleChange(e);
                refCode2.current.focus();
              }} />
            <input
              className='segmented-input big-input'
              ref={refCode2}
              type="text"
              inputMode="numeric"
              autoComplete="false"
              maxLength="1"
              size="1"
              name="gameCode2"
              value={formik.values.gameCode2}
              onBlur={formik.handleBlur}
              onFocus={e => {
                formik.setFieldValue('gameCode2', '');
              }}
              onKeyDown={e => {
                if (e.key === "Backspace") {
                  refCode1.current.focus();
                }
              }}

              onChange={e => {
                if (isNaN(e.target.value)) {
                  return;
                }
                formik.handleChange(e);
                refCode3.current.focus();
              }} />
            <input
              className='segmented-input big-input'
              ref={refCode3}
              type="text"
              inputMode="numeric"
              autoComplete="false"
              maxLength="1"
              size="1"
              name="gameCode3"
              value={formik.values.gameCode3}
              onBlur={formik.handleBlur}
              onFocus={e => {
                formik.setFieldValue('gameCode3', '');
              }}
              onKeyDown={e => {
                if (e.key === "Backspace") {
                  refCode2.current.focus();
                }
              }}
              onChange={e => {
                if (isNaN(e.target.value)) {
                  return;
                }
                formik.handleChange(e);
                refCode4.current.focus();
              }} />
            <input
              className='segmented-input big-input'
              ref={refCode4}
              type="text"
              inputMode="numeric"
              autoComplete="false"
              maxLength="1"
              size="1"
              name="gameCode4"
              value={formik.values.gameCode4}
              onBlur={formik.handleBlur}
              onFocus={e => {
                formik.setFieldValue('gameCode4', '');
              }}
              onKeyDown={e => {
                if (e.key === "Backspace") {
                  refCode3.current.focus();
                }
              }}
              onChange={e => {
                if (isNaN(e.target.value)) {
                  return;
                }
                formik.handleChange(e);
                setTimeout(() => {
                  refCode4.current.blur();
                }, 10);
              }} />
          </div>
          <div className="help speechBalloonSmall">
            <p>{t("student.game_code.help_text")}</p>
          </div>
        </div>
        <div className={`navigationDrawer formFooter spread variant6 `} style={{top: footerTopPosition}}>
          <a className="chromelessIconButton" onClick={handleBackButton}>
            <span className="icon icon-backward-simple"></span>
          </a>
          <button type="submit" className={`chromelessIconButton ${!formik.isValid || formik.isSubmitting ? 'disabled' : ''}`} disabled={!formik.isValid || formik.isSubmitting}>
            <span className="caption">{t("student.game_code.continue_button")}</span>
            <span className={`icon ${!formik.isValid || formik.isSubmitting ? 'icon-forward-simple' : 'icon-forward'}`}></span>
          </button>
        </div>

      </form>

      <div className={`popup-wrapper ${showErrorPopup ? 'show' : ''}`}>
        <div className="popup-message" ref={e => { messageRef = e }}>
          <h3>{t("student.game_code.error_title")}</h3>
          <p>{t("student.game_code.error_message")}</p>
          <button onClick={hideErrorPopup}>{t("student.game_code.error_button")}</button>
        </div>
      </div>
    </div>
  );
}

export default Onboard;